// @ts-nocheck
/**
 * LiveBackendStatus — proof-of-life banner for the Command Center.
 *
 * Fetches /voters/stats and /voters/by-precinct?limit=1 on mount and
 * renders a small header strip showing:
 *   - connection state (green/yellow/red dot)
 *   - real total_voters count from Postgres
 *   - live tier breakdown (SUPER_VOTER / HOT / WARM / COOL)
 *   - top precinct (the one with the most registered voters)
 *   - elapsed fetch time so you can visually confirm it's talking to a
 *     real server, not a cached static file
 *
 * Graceful degradation:
 *   - 401 (no JWT yet) → yellow banner: "Not logged in — showing
 *     static snapshot".
 *   - Connection error → red banner: "Backend unreachable".
 *   - Success → green banner with the real counts.
 *
 * This is intentionally the smallest possible vertical slice that
 * proves the 78k import is reachable from the UI. Deep wiring of
 * the individual dashboard views comes next.
 */
import { useEffect, useState } from 'react';
import client from '../api/client';

type Stats = {
  total_voters: number;
  average_score: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  primary_voters: number;
  high_scorers: number;
};

type PrecinctRollup = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  avg_score: number;
  super_voters: number;
  hot: number;
  warm: number;
  cool: number;
  super_voter_pct: number;
};

type Status = 'loading' | 'ok' | 'unauthenticated' | 'error';

export function LiveBackendStatus() {
  const [status, setStatus] = useState<Status>('loading');
  const [stats, setStats] = useState<Stats | null>(null);
  const [topPrecinct, setTopPrecinct] = useState<PrecinctRollup | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const [errMsg, setErrMsg] = useState<string>('');

  useEffect(() => {
    const run = async () => {
      const t0 = performance.now();
      try {
        const [statsRes, precinctRes] = await Promise.all([
          client.get<Stats>('/voters/stats'),
          client.get<PrecinctRollup[]>('/voters/by-precinct', { params: { limit: 1 } }),
        ]);
        setStats(statsRes.data);
        setTopPrecinct(precinctRes.data[0] ?? null);
        setStatus('ok');
      } catch (err: any) {
        const code = err?.response?.status;
        if (code === 401) {
          setStatus('unauthenticated');
          setErrMsg('Not logged in — showing static snapshot');
        } else if (err?.code === 'ERR_NETWORK' || !err?.response) {
          setStatus('error');
          setErrMsg(`Backend unreachable at ${client.defaults.baseURL}`);
        } else {
          setStatus('error');
          setErrMsg(`API error ${code}: ${err?.response?.data?.detail ?? err?.message}`);
        }
      } finally {
        setElapsedMs(Math.round(performance.now() - t0));
      }
    };
    run();
  }, []);

  // Connectivity/error states are handled globally by <BackendErrorBanner />.
  // Here we only render the live stat grid once real data is in hand.
  if (status !== 'ok' || !stats) return null;

  return (
    <div className="mb-4 rounded-lg border border-border-base bg-bg-raised/60 px-4 py-3 shadow-ds-md">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
        <Stat label="Total voters" value={stats.total_voters.toLocaleString()} highlight />
        <Stat label="Avg score" value={stats.average_score.toFixed(1)} />
        <Stat
          label="Super voters"
          value={(stats.by_tier.SUPER_VOTER ?? 0).toLocaleString()}
        />
        <Stat label="Hot" value={(stats.by_tier.HOT ?? 0).toLocaleString()} />
        <Stat label="Warm" value={(stats.by_tier.WARM ?? 0).toLocaleString()} />
        <Stat label="Cool" value={(stats.by_tier.COOL ?? 0).toLocaleString()} />
        {topPrecinct && (
          <div className="col-span-2 sm:col-span-3 lg:col-span-6 mt-1 text-xs text-fg-muted">
            Top precinct: <span className="text-fg-secondary font-medium">{topPrecinct.precinct_name}</span> —
            {' '}{topPrecinct.total_voters.toLocaleString()} voters,
            {' '}avg score {topPrecinct.avg_score.toFixed(1)},
            {' '}{topPrecinct.super_voters} super-voters
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="rounded bg-bg-base/60 border border-border-base px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">{label}</div>
      <div className={`mt-0.5 font-semibold ${highlight ? 'text-accent-fg text-lg' : 'text-fg-primary'}`}>
        {value}
      </div>
    </div>
  );
}
